/**
 * «Личное дело» (interactives.md H1): экран-документ в стиле свидетельства
 * (двойная рамка, dt/dd, данные в моно), который открывает кнопка-имя в
 * шапке экрана жизни. Только чтение `Life`; все строки — через `t()`.
 *
 * Секции: кто (имя, пол, возраст и год, страна и откуда родом, город/село),
 * внешность (`life.look`), семья (родители, братья и сёстры, партнёр, дети
 * с возрастами), образование, работа (должность, уровень, стаж, доход),
 * деньги (сбережения, долг, доход, расходы — по `EXPENSE_SHARE` движка),
 * четыре состояния числом и словом, привычки и болезни (тяга к игре —
 * словами по `urgeOf`), документы и имущество (права, жильё, машина,
 * вложения, заём), мечта и вехи; внизу — карта жизни и ☆-годы.
 *
 * Деньги показаны в долларах ППС и «ощущаемых» рублях (`money`). Слова
 * состояний — не оценка, а описание: цвет не используется.
 */
import type { Dataset } from '@life/data';
import {
  CHILD_EXPENSE_SHARE,
  CONDITION_CHRONIC,
  CONDITION_HIV,
  EXPENSE_SHARE,
  FLAG_CRYPTO_HELD,
  FLAG_JOB_START_AGE,
  FLAG_LICENSE,
  FLAG_LOAN_DEFAULTED,
  FLAG_LOAN_DUE,
  FLAG_PYRAMID_INVESTED,
  FLAG_STOCKS_HELD,
  MINOR_ILLNESS_FLAG,
  URGE_THRESHOLD,
  YEAR_SUMMARY_KEY,
  clampStat,
  dreamProgress,
  urgeOf,
  type Life,
  type Stats,
} from '@life/engine';
import { t, yearsWord } from '@life/i18n';
import { Fragment } from 'preact';
import { LifeMap } from '../components/LifeMap';
import { ThemeToggle } from '../components/ThemeToggle';
import { birthCountry, countryLabel } from '../lib/certificate';
import { childrenWord, money, years } from '../lib/format';
import type { Game } from '../state/game';

export interface DossierScreenProps {
  game: Game;
  ds: Dataset;
}

type Row = [string, string];
type Section = { key: string; rows: Row[] };

const STAT_KEYS: readonly (keyof Stats)[] = ['health', 'happiness', 'knowledge', 'relations'];
/** Границы слов о состоянии: ниже — «мало», выше — «много». */
export const STAT_WORD_LOW = 40;
export const STAT_WORD_HIGH = 70;

function statWord(k: keyof Stats, v: number): string {
  const band = v < STAT_WORD_LOW ? 'low' : v < STAT_WORD_HIGH ? 'mid' : 'high';
  return t(`ui.dossier.stat.${k}.${band}`);
}

function num(life: Life, flag: string): number {
  const v = life.flags[flag];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

const none = (): string => t('ui.dossier.none');

function personRows(life: Life, ds: Dataset): Row[] {
  const country = countryLabel(ds, life.iso3);
  const from = birthCountry(life, ds);
  return [
    [t('ui.dossier.sex'), t(`ui.dossier.sex.${life.sex}`)],
    [t('ui.dossier.age'), t('ui.dossier.age.value', { n: life.age, years: yearsWord(life.age), year: life.birthYear + life.age })],
    [t('ui.dossier.country'), from === country ? country : t('ui.dossier.country.from', { country, from })],
    [t('ui.dossier.place'), t(life.urban ? 'ui.birth.urban' : 'ui.birth.rural')],
  ];
}

function lookRows(life: Life): Row[] {
  const { look } = life;
  return [
    [t('ui.dossier.height'), t('ui.dossier.height.value', { n: Math.round(look.heightCm) })],
    [t('ui.dossier.build'), t(`look.build.${look.build}`)],
    [t('ui.dossier.hair'), t(look.hairKey)],
    [t('ui.dossier.eyes'), t(look.eyesKey)],
    [t('ui.dossier.mark'), t(look.markKey)],
  ];
}

function familyRows(life: Life): Row[] {
  const parents = Math.max(0, Math.min(2, Math.trunc(life.parentsAlive)));
  const children =
    life.children > 0
      ? t('ui.dossier.children.list', {
          n: life.children,
          word: childrenWord(life.children),
          ages: life.childrenAges.map((a) => t('ui.dossier.children.age', { n: a, years: yearsWord(a) })).join(', '),
        })
      : none();
  return [
    [t('ui.dossier.parents'), t(`ui.dossier.parents.${parents}`)],
    [t('ui.dossier.siblings'), life.siblings > 0 ? String(life.siblings) : none()],
    [t('ui.dossier.partner'), life.partner ? t(`ui.death.family.partner.${life.sex}`) : none()],
    [t('ui.dossier.children'), children],
  ];
}

function studyRows(life: Life): Row[] {
  return [
    [t('ui.dossier.education'), t(`education.${life.education}`)],
    [t('ui.dossier.school'), t(life.inSchool ? 'ui.dossier.school.yes' : 'ui.dossier.school.no')],
  ];
}

function workRows(life: Life): Row[] {
  const job = life.job;
  if (job === null) return [[t('ui.dossier.job'), t('ui.dossier.job.none')]];
  const started = life.flags[FLAG_JOB_START_AGE];
  const n = typeof started === 'number' ? Math.max(1, life.age - started) : 1;
  return [
    [t('ui.dossier.job'), t(job.titleKey)],
    [t('ui.dossier.job.level'), String(job.level)],
    [t('ui.dossier.job.years.label'), t('ui.dossier.job.years', { n, years: yearsWord(n) })],
    [t('ui.dossier.income'), money(job.income)],
  ];
}

function moneyRows(life: Life): Row[] {
  const income = life.job?.income ?? 0;
  const expenses = income * (EXPENSE_SHARE + CHILD_EXPENSE_SHARE * Math.max(0, life.children));
  return [
    [t('ui.dossier.savings'), money(life.money)],
    [t('ui.dossier.debt'), money(life.debt)],
    [t('ui.dossier.income'), life.job ? money(income) : t('ui.dossier.job.none')],
    [t('ui.dossier.expenses'), money(Math.round(expenses))],
  ];
}

function statRows(life: Life): Row[] {
  return STAT_KEYS.map((k) => {
    const v = Math.round(clampStat(life.stats[k]));
    return [t(`ui.stats.${k}`), t('ui.dossier.stat.value', { n: v, word: statWord(k, v) })];
  });
}

function healthRows(life: Life): Row[] {
  const urge = urgeOf(life);
  const craving = urge >= URGE_THRESHOLD ? t('ui.dossier.craving.strong') : urge > 0 ? t('ui.dossier.craving.some') : '';
  const gambling = life.habits.gambling ? t('ui.dossier.habit.yes') : t('ui.dossier.habit.no');
  const conditions: string[] = [];
  if (life.conditions.includes(CONDITION_CHRONIC)) conditions.push(t('ui.dossier.condition.chronic'));
  if (life.conditions.includes(CONDITION_HIV)) conditions.push(t('ui.dossier.condition.hiv'));
  if (life.flags[MINOR_ILLNESS_FLAG] === life.birthYear + life.age) conditions.push(t('ui.dossier.illness'));
  return [
    [t('ui.dossier.smoking'), t(life.habits.smoking ? 'ui.dossier.habit.yes' : 'ui.dossier.habit.no')],
    [t('ui.dossier.alcohol'), t(life.habits.alcohol ? 'ui.dossier.habit.yes' : 'ui.dossier.habit.no')],
    [t('ui.dossier.gambling'), craving ? `${gambling}, ${craving}` : gambling],
    [t('ui.dossier.conditions'), conditions.length > 0 ? conditions.join(', ') : none()],
  ];
}

function assetRows(life: Life): Row[] {
  const housing = life.assets.housing === 'own' ? t('ui.dossier.housing.own') : t(`assets.housing.${life.assets.housing}`);
  const holdings: string[] = [];
  const stocks = num(life, FLAG_STOCKS_HELD);
  const crypto = num(life, FLAG_CRYPTO_HELD);
  const pyramid = num(life, FLAG_PYRAMID_INVESTED);
  if (stocks > 0) holdings.push(t('ui.dossier.holdings.stocks', { sum: money(stocks) }));
  if (crypto > 0) holdings.push(t('ui.dossier.holdings.crypto', { sum: money(crypto) }));
  if (pyramid > 0) holdings.push(t('ui.dossier.holdings.pyramid', { sum: money(pyramid) }));
  const due = num(life, FLAG_LOAN_DUE);
  const loan =
    life.flags[FLAG_LOAN_DEFAULTED] === true ? t('ui.dossier.loan.defaulted') : due > 0 ? t('ui.dossier.loan.due', { sum: money(due) }) : none();
  return [
    [t('ui.dossier.license'), life.flags[FLAG_LICENSE] === true ? t('ui.dossier.yes') : none()],
    [t('ui.dossier.housing'), housing],
    [t('ui.dossier.car'), life.assets.car ? t('ui.dossier.yes') : none()],
    [t('ui.dossier.holdings'), holdings.length > 0 ? holdings.join('; ') : none()],
    [t('ui.dossier.loan'), loan],
  ];
}

function goalRows(life: Life, ds: Dataset): Row[] {
  const dream = life.dream;
  const dreamText =
    dream === null
      ? t('dream.none')
      : dream.achievedAt !== null
        ? t('ui.death.dream.done', { dream: t(`dream.${dream.id}`), age: dream.achievedAt, years: yearsWord(dream.achievedAt) })
        : t('ui.dossier.dream.progress', { dream: t(`dream.${dream.id}`), percent: Math.round(dreamProgress(life, ds) * 100) });
  return [
    [t('ui.dossier.dream'), dreamText],
    [
      t('ui.dossier.milestones'),
      life.milestones.length > 0 ? life.milestones.map((id) => t(`milestone.${id}`)).join(' ') : t('ui.dossier.milestones.none'),
    ],
  ];
}

export function DossierScreen({ game, ds }: DossierScreenProps) {
  const life = game.life.value;
  if (life === null) return null;
  const sections: Section[] = [
    { key: 'person', rows: personRows(life, ds) },
    { key: 'look', rows: lookRows(life) },
    { key: 'family', rows: familyRows(life) },
    { key: 'study', rows: studyRows(life) },
    { key: 'work', rows: workRows(life) },
    { key: 'money', rows: moneyRows(life) },
    { key: 'stats', rows: statRows(life) },
    { key: 'health', rows: healthRows(life) },
    { key: 'assets', rows: assetRows(life) },
    { key: 'goals', rows: goalRows(life, ds) },
  ];
  const starred = life.log.filter((e) => e.starred && e.key === YEAR_SUMMARY_KEY).map((e) => e.age);

  return (
    <main class="page dossier-page">
      <header class="topbar">
        <b>{t('ui.app.title')}</b>
        <div class="topbar-tools">
          <ThemeToggle game={game} />
          <button type="button" class="btn" onClick={() => game.closeDossier()}>
            {t('ui.dossier.back')}
          </button>
        </div>
      </header>
      <section class="cert dossier">
        <div class="eyebrow">{t('ui.dossier.eyebrow')}</div>
        <h1>{life.name}</h1>
        <div class="sub">{years(life.age)}</div>
        {sections.map((s) => (
          <div class="dossier-section" key={s.key}>
            <div class="eyebrow">{t(`ui.dossier.section.${s.key}`)}</div>
            <dl>
              {s.rows.map(([k, v]) => (
                <Fragment key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </Fragment>
              ))}
            </dl>
          </div>
        ))}
        <div class="dossier-section">
          <div class="eyebrow">{t('ui.dossier.section.map')}</div>
          <LifeMap life={life} />
          <dl>
            <dt>{t('ui.dossier.starred')}</dt>
            <dd>{starred.length > 0 ? starred.join(', ') : none()}</dd>
          </dl>
        </div>
      </section>
      <div class="dossier-foot">
        <button type="button" class="btn primary block" onClick={() => game.closeDossier()}>
          {t('ui.dossier.back')}
        </button>
      </div>
    </main>
  );
}
